import {
  Brush as BrushIcon,
  CloudDone as CloudIcon,
  FlashOn as FlashIcon,
  Psychology as BrainIcon,
  Rocket as RocketIcon,
  Security as SecurityIcon,
  TrendingUp as TrendingIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import { useEffect, useState } from 'react';

// Import images
import featuresImage from '../assets/flow.webp';

const FeaturesMinimal = () => {
  const [visibleCards, setVisibleCards] = useState(new Set());

  // Light and soft feature data
  const features = [
    {
      id: 1,
      icon: <BrainIcon />,
      title: "AI Destekli İçerik",
      subtitle: "Yapay Zeka ile Hızlı Üretim",
      description: "Gelişmiş AI teknolojisi ile içeriklerinizi otomatik optimize edin. Başlık önerileri, SEO iyileştirmeleri ve otomatik etiketleme.",
      details: [
        "Otomatik başlık önerileri",
        "SEO skorlama sistemi", 
        "Akıllı etiketleme",
        "İçerik analizi"
      ],
      color: "#3b82f6",
      lightColor: "#dbeafe"
    },
    {
      id: 2,
      icon: <FlashIcon />,
      title: "Hızlı Performans",
      subtitle: "Milisaniyelerle Ölçülen Hız",
      description: "Next.js ve modern teknolojiler ile optimize edilmiş performans. Sayfa yükleme süreleri 300ms altında.",
      details: [
        "300ms altı yükleme",
        "CDN entegrasyonu",
        "Otomatik optimizasyon",
        "Lazy loading"
      ],
      color: "#f59e0b",
      lightColor: "#fef3c7"
    },
    {
      id: 3,
      icon: <SecurityIcon />,
      title: "Güvenli Altyapı",
      subtitle: "Bank Seviyesinde Koruma",
      description: "SSL şifreleme, çok faktörlü kimlik doğrulama ve gelişmiş güvenlik duvarı ile verileriniz tamamen güvende.",
      details: [
        "SSL şifreleme",
        "2FA güvenlik",
        "Güvenlik duvarı",
        "Otomatik yedekleme"
      ],
      color: "#10b981",
      lightColor: "#d1fae5"
    },
    {
      id: 4,
      icon: <BrushIcon />,
      title: "Kolay Özelleştirme",
      subtitle: "Hayal Ettiğiniz Tasarım",
      description: "Drag & drop editör ile kod bilmeden profesyonel tasarımlar. 1000+ şablon ve sınırsız özelleştirme seçeneği.",
      details: [
        "Drag & drop editör",
        "1000+ şablon",
        "Renk paleti",
        "Font kütüphanesi"
      ],
      color: "#8b5cf6",
      lightColor: "#ede9fe"
    },
    {
      id: 5,
      icon: <TrendingIcon />,
      title: "Detaylı Analitik",
      subtitle: "Veriler Konuşuyor",
      description: "Gerçek zamanlı analitik raporları ile performansınızı takip edin. Ziyaretçi davranışları ve içerik başarı metrikleri.",
      details: [
        "Gerçek zamanlı raporlar",
        "Kullanıcı davranışları",
        "İçerik metrikleri",
        "SEO performansı"
      ],
      color: "#ef4444",
      lightColor: "#fee2e2"
    },
    {
      id: 6,
      icon: <CloudIcon />,
      title: "Bulut Teknolojisi",
      subtitle: "Her Yerden Erişim",
      description: "Bulut tabanlı altyapı ile her cihazdan erişim. Otomatik yedekleme ve senkronizasyon ile verileriniz her zaman güvende.",
      details: [
        "Bulut depolama",
        "Otomatik senkronizasyon",
        "Çoklu cihaz desteği",
        "Offline çalışma"
      ],
      color: "#06b6d4",
      lightColor: "#cffafe"
    }
  ];

  // Simple intersection observer for gentle animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index);
            setTimeout(() => {
              setVisibleCards(prev => new Set([...prev, index]));
            }, index * 100); // Gentle stagger
          }
        });
      },
      { threshold: 0.2 }
    );

    const cards = document.querySelectorAll('[data-index]');
    cards.forEach(card => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(180deg, #fafafa 0%, #ffffff 100%)',
        color: '#1f2937'
      }}
    >
      <Container maxWidth="xl">
        {/* Simple Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 700,
              mb: 3,
              color: '#1f2937',
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif'
            }}
          >
            Güçlü Özellikler
          </Typography>

          <Typography
            variant="h5"
            sx={{
              fontSize: { xs: '1.2rem', md: '1.3rem' },
              color: '#6b7280',
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 400
            }}
          >
            Modern teknolojiler ve kullanıcı dostu çözümlerle projelerinizi kolayca yönetin
          </Typography>
        </Box>

        {/* Clean Features Grid */}
        <Grid container spacing={4} sx={{ mb: 10 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} lg={4} key={feature.id}>
              <Paper
                data-index={index}
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  background: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 3,
                  position: 'relative',
                  cursor: 'pointer',
                  opacity: visibleCards.has(index) ? 1 : 0,
                  transform: visibleCards.has(index) ? 'translateY(0)' : 'translateY(20px)',
                  transition: 'all 0.5s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08)',
                    borderColor: feature.color
                  }
                }}
              >
                {/* Simple Feature Icon */}
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                    width: 64,
                    height: 64,
                    background: feature.lightColor,
                    borderRadius: 2,
                    color: feature.color
                  }}
                >
                  <Box sx={{ fontSize: '2rem' }}>
                    {feature.icon}
                  </Box>
                </Box>

                {/* Clean Content */}
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    mb: 1.5,
                    color: '#1f2937',
                    fontSize: '1.4rem',
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif'
                  }}
                >
                  {feature.title}
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 500,
                    mb: 3,
                    color: feature.color,
                    fontSize: '1rem'
                  }}
                >
                  {feature.subtitle}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: '#6b7280',
                    lineHeight: 1.6,
                    mb: 4,
                    fontSize: '0.95rem'
                  }}
                >
                  {feature.description}
                </Typography>

                {/* Simple Feature List */}
                <Stack spacing={1.5} sx={{ mb: 4 }}>
                  {feature.details.map((detail, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          background: feature.color
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#4b5563',
                          fontSize: '0.9rem'
                        }}
                      >
                        {detail}
                      </Typography>
                    </Box>
                  ))}
                </Stack>

                {/* Simple Button */}
                <Button
                  variant="outlined"
                  sx={{
                    borderColor: feature.color,
                    color: feature.color,
                    fontWeight: 500,
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '0.9rem',
                    '&:hover': {
                      borderColor: feature.color,
                      bgcolor: feature.lightColor
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  Detayları Gör
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Simple Call to Action */}
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 4,
            background: '#f9fafb',
            borderRadius: 4,
            border: '1px solid #e5e7eb'
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 700,
              mb: 3,
              color: '#1f2937'
            }}
          >
            Başlamaya Hazır mısınız?
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: '#6b7280',
              mb: 5,
              maxWidth: 500,
              mx: 'auto',
              fontSize: '1.1rem',
              fontWeight: 400
            }}
          >
            Tüm bu güçlü özellikleri hemen deneyimlemeye başlayın
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={3}
            sx={{ justifyContent: 'center' }}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<RocketIcon />}
              sx={{
                background: '#3b82f6',
                color: 'white',
                fontWeight: 600,
                px: 6,
                py: 2.5,
                fontSize: '1rem',
                borderRadius: 2,
                textTransform: 'none',
                boxShadow: '0 4px 14px rgba(59, 130, 246, 0.3)',
                '&:hover': {
                  background: '#2563eb',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              Ücretsiz Deneyin
            </Button>

            <Button
              variant="outlined"
              size="large"
              startIcon={<VisibilityIcon />}
              sx={{
                borderColor: '#d1d5db',
                color: '#6b7280',
                fontWeight: 500,
                px: 6,
                py: 2.5,
                fontSize: '1rem',
                borderRadius: 2,
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#9ca3af',
                  bgcolor: '#f9fafb',
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              Demo İzle
            </Button>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default FeaturesMinimal;
